import React, { useEffect, useState, useCallback } from 'react';
import { ShieldCheck, ShieldAlert, FileDown, Search, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { useApi, useAuth } from '@/contexts/AuthContext';

const C = { navy: '#0D1B2A', slate: '#5C6B7A', green: '#1B7A3D', red: '#B0413E', blue: '#2A6FC4', amber: '#8A6D1D', line: '#E4E9EF' };

const TYPE_COLORS = { quote_decided: '#2A6FC4', bind: '#1B7A3D', cancel: '#B0413E', endorse: '#8A6D1D', renew: '#5B4FA0', correction: '#5C6B7A' };

function DuplicatesCard({ api, onChanged }) {
  const [d, setD] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.get('/ledger/duplicates').then(r => setD(r.data)).catch(() => {}); }, [api]);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  const open = (d.duplicate_bind_events || 0) + (d.duplicate_transactions || 0);
  const last = (d.runs || [])[0];
  const supersede = async () => {
    setBusy(true);
    try {
      const r = await api.post('/ledger/duplicates/supersede', { reason });
      toast.success(`${r.data.events} events superseded by correction ${String(r.data.correction_event_id || '').slice(0, 8)}; ${r.data.transactions} twin transactions stamped`);
      setReason(''); load(); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Supersede failed'); }
    setBusy(false);
  };
  return (
    <div className="p-4 rounded-lg border" style={{ borderColor: C.line, background: '#fff', borderLeft: `3px solid ${open ? C.amber : C.green}` }} data-testid="ledger-duplicates-card">
      <div className="flex flex-wrap items-center gap-4">
        <Stat label="Duplicate bind events (open)" value={(d.duplicate_bind_events || 0).toLocaleString()} testid="ledger-duplicate-binds" />
        <Stat label="Twin transactions (open)" value={(d.duplicate_transactions || 0).toLocaleString()} testid="ledger-duplicate-transactions" />
        <Stat label="Superseded, kept on the chain" value={(d.already_superseded || 0).toLocaleString()} testid="ledger-superseded-count" />
        {last && <Stat label="Last supersede run" value={`${last.run_id} · ${String(last.created_at || '').slice(0, 10)} · ${last.events} events`} mono testid="ledger-last-supersede-run" />}
        {open > 0 && (
          <span className="ml-auto flex items-center gap-2">
            <input value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason (10+ chars)" data-testid="ledger-supersede-reason"
              className="px-2 py-1.5 text-xs rounded-md border outline-none w-64" style={{ borderColor: C.line, color: C.navy }} />
            <button onClick={supersede} disabled={busy || reason.trim().length < 10} data-testid="ledger-supersede-btn"
              className="px-3 py-1.5 rounded-md text-white text-xs font-semibold disabled:opacity-50" style={{ background: C.navy }}>
              Supersede with one correction event
            </button>
          </span>
        )}
      </div>
      <p className="text-[11px] mt-2" style={{ color: C.slate }}>{d.note} — the ledger is append-only: nothing is deleted, one correction event names every twin and every reader marks them.</p>
    </div>
  );
}

export default function LedgerPage() {
  const api = useApi();
  const { user } = useAuth();
  const isAdmin = ['platform_owner', 'mga_admin'].includes(user?.role);
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [q, setQ] = useState('');
  const [eventType, setEventType] = useState('');
  const [evidence, setEvidence] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (query = '', type = '') => {
    try {
      const [s, e] = await Promise.all([
        api.get('/ledger/summary'),
        api.get(`/ledger/events?q=${encodeURIComponent(query)}&event_type=${type}&limit=50`),
      ]);
      setSummary(s.data);
      setEvents(e.data.events || []);
    } catch { toast.error('Failed to load ledger'); }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setTimeout(() => load(q, eventType), 350);
    return () => clearTimeout(t);
  }, [q, eventType, load]);

  const openEvidence = async (eventId) => {
    try {
      const r = await api.get(`/ledger/evidence/${eventId}`);
      setEvidence(r.data);
    } catch { toast.error('Failed to build evidence pack'); }
  };

  const downloadPdf = async (eventId, seq) => {
    try {
      const r = await api.get(`/ledger/evidence/${eventId}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `evidence_pack_${seq}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Evidence pack exported and registered in the DMS');
    } catch { toast.error('Export failed'); }
  };

  const downloadSoR = async () => {
    try {
      const r = await api.get('/agent-governance/system-of-record', { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'Platform_System_of_Record.md'; a.click();
      URL.revokeObjectURL(url);
      toast.success('System of Record downloaded');
    } catch { toast.error('Download failed'); }
  };

  useEffect(() => {
    if (!evidence) return;
    const onKey = (e) => { if (e.key === 'Escape') setEvidence(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [evidence]);

  if (loading) return <div className="p-8 text-sm" style={{ color: C.slate }}>Replaying chain…</div>;

  return (
    <div className="space-y-5" data-testid="ledger-page">
      {summary && (
        <div className="flex flex-wrap items-center gap-4 p-4 rounded-lg border" style={{ borderColor: C.line, background: '#fff' }} data-testid="ledger-summary">
          {summary.chain_verified
            ? <span className="flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-bold text-white" style={{ background: C.green }} data-testid="chain-verified-badge"><ShieldCheck size={14} /> CHAIN VERIFIED · {summary.events_checked.toLocaleString()} events replayed</span>
            : <span className="flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-bold text-white" style={{ background: C.red }} data-testid="chain-broken-badge"><ShieldAlert size={14} /> CHAIN BROKEN at seq {summary.first_break_seq}</span>}
          <Stat label="Total events" value={summary.total_events.toLocaleString()} />
          <Stat label="Live" value={summary.live_events.toLocaleString()} />
          <Stat label="Backfilled (historic)" value={summary.backfilled_events.toLocaleString()} />
          <Stat label="Superseded" value={(summary.superseded_events || 0).toLocaleString()} testid="ledger-summary-superseded" />
          <Stat label="Head hash" value={`${(summary.head?.hash || '').slice(0, 12)}…`} mono />
          <button onClick={downloadSoR} className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border hover:bg-slate-50" style={{ borderColor: C.line, color: C.navy }} data-testid="download-system-of-record-btn">
            <FileDown size={13} /> System of Record (.md)
          </button>
        </div>
      )}
      {isAdmin && <DuplicatesCard api={api} onChanged={() => load(q, eventType)} />}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[260px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: C.slate }} />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by submission, policy, entity, decision or reason code…"
            data-testid="ledger-search-input"
            className="w-full pl-9 pr-3 py-2 text-sm rounded-md border outline-none focus:border-[#2A6FC4]"
            style={{ borderColor: C.line, color: C.navy }} />
        </div>
        <select value={eventType} onChange={e => setEventType(e.target.value)} data-testid="ledger-type-filter"
          className="px-3 py-2 text-sm rounded-md border outline-none" style={{ borderColor: C.line, color: C.navy }}>
          <option value="">All event types</option>
          {['quote_decided', 'bind', 'cancel', 'endorse', 'renew', 'correction'].map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
        </select>
      </div>

      <div className="rounded-lg border overflow-hidden" style={{ borderColor: C.line, background: '#fff' }}>
        <table className="w-full text-xs">
          <thead>
            <tr style={{ background: '#F5F7FA', color: C.slate }}>
              {['Seq', 'Event', 'Decision', 'Reason codes', 'Reference', 'Decided at', 'Hash', ''].map(h => <th key={h} className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[10px]">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {events.map(e => (
              <tr key={e.event_id} className="border-t hover:bg-[#F8FAFC]" style={{ borderColor: '#EEF1F5' }} data-testid={`ledger-event-${e.chain.seq}`}>
                <td className="px-3 py-2 font-mono" style={{ color: C.slate }}>{e.chain.seq}</td>
                <td className="px-3 py-2"><span className="px-2 py-0.5 rounded text-white text-[10px] font-semibold" style={{ background: TYPE_COLORS[e.event_type] || C.slate }}>{e.event_type}</span>{e.backfilled && <span className="ml-1 text-[9px]" style={{ color: C.slate }}>historic</span>}{e.superseded && <span className="ml-1 text-[9px] font-semibold" style={{ color: C.amber }} data-testid={`ledger-superseded-${e.chain.seq}`}>superseded</span>}</td>
                <td className="px-3 py-2 font-semibold" style={{ color: C.navy }}>{e.decision || '—'}</td>
                <td className="px-3 py-2" style={{ color: C.slate }}>{(e.reason_codes || []).slice(0, 2).join(', ') || '—'}</td>
                <td className="px-3 py-2 font-mono" style={{ color: C.slate }}>{(e.refs?.policy_id || e.refs?.submission_id || '').slice(0, 8)}</td>
                <td className="px-3 py-2" style={{ color: C.slate }}>{(e.decided_at || '').slice(0, 19).replace('T', ' ')}</td>
                <td className="px-3 py-2 font-mono" style={{ color: C.slate }}>{e.chain.hash.slice(0, 10)}…</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <button onClick={() => openEvidence(e.event_id)} className="text-[11px] font-semibold mr-3 hover:underline" style={{ color: C.blue }} data-testid={`ledger-verify-${e.chain.seq}`}><Link2 size={11} className="inline mr-1" />Verify</button>
                  <button onClick={() => downloadPdf(e.event_id, e.chain.seq)} className="text-[11px] font-semibold hover:underline" style={{ color: C.navy }} data-testid={`ledger-export-${e.chain.seq}`}><FileDown size={11} className="inline mr-1" />Evidence PDF</button>
                </td>
              </tr>
            ))}
            {events.length === 0 && <tr><td colSpan={8} className="px-3 py-6 text-center" style={{ color: C.slate }}>No ledger events match.</td></tr>}
          </tbody>
        </table>
      </div>

      {evidence && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6" style={{ background: 'rgba(13,27,42,0.5)' }} onClick={() => setEvidence(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="evidence-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold" style={{ color: C.navy }}>Evidence — event {evidence.event.chain.seq} ({evidence.event.event_type})</h3>
              <div className="flex items-center gap-2">
                {evidence.hash_proof.link_verified && evidence.full_chain.verified
                  ? <span className="px-2 py-1 rounded text-white text-[10px] font-bold" style={{ background: C.green }} data-testid="evidence-verified-badge">VERIFIED</span>
                  : <span className="px-2 py-1 rounded text-white text-[10px] font-bold" style={{ background: C.red }}>FAILED</span>}
                <button onClick={() => setEvidence(null)} className="text-lg leading-none px-1" style={{ color: C.slate }} data-testid="evidence-close-btn" aria-label="Close">×</button>
              </div>
            </div>
            <div className="space-y-2 text-xs" style={{ color: C.navy }}>
              <Row k="Decision" v={`${evidence.event.decision || '—'} · ${(evidence.event.reason_codes || []).join(', ') || 'no reason codes'}`} />
              <Row k="Decided by" v={`${evidence.event.decided_by?.kind} / ${evidence.event.decided_by?.id}${evidence.event.decided_by?.version ? ' v' + evidence.event.decided_by.version : ''}`} />
              <Row k="Rule versions" v={JSON.stringify(evidence.event.rule_versions || {})} mono />
              <Row k="Stored hash" v={evidence.hash_proof.stored_hash} mono />
              <Row k="Recomputed hash" v={evidence.hash_proof.recomputed_hash} mono />
              <Row k="Hash matches" v={String(evidence.hash_proof.event_hash_matches)} />
              <Row k="Chain links (prev / next)" v={`${evidence.hash_proof.prev_link_ok} / ${evidence.hash_proof.next_link_ok}`} />
              <Row k="Full chain replay" v={`${evidence.full_chain.verified ? 'verified' : 'BROKEN'} — ${evidence.full_chain.events_checked.toLocaleString()} events`} />
              <Row k="Related events on this risk" v={String(evidence.related_events.length)} />
              {evidence.event.input_payload && (
                <div><p className="font-semibold mt-3 mb-1" style={{ color: C.slate }}>Decision inputs</p>
                  <pre className="p-2 rounded text-[10px] overflow-x-auto" style={{ background: '#F5F7FA', border: `1px solid ${C.line}` }}>{JSON.stringify(evidence.event.input_payload, null, 1).slice(0, 3000)}</pre></div>
              )}
            </div>
            <button onClick={() => downloadPdf(evidence.event.event_id, evidence.event.chain.seq)}
              className="mt-4 px-4 py-2 rounded-md text-white text-xs font-semibold" style={{ background: C.navy }}
              data-testid="evidence-export-pdf-btn"><FileDown size={12} className="inline mr-1" />Export Evidence Pack (PDF)</button>
          </div>
        </div>
      )}
    </div>
  );
}

const Stat = ({ label, value, mono, testid }) => (
  <div>
    <p className="text-[10px] uppercase tracking-wider" style={{ color: C.slate }}>{label}</p>
    <p className={`text-sm font-bold ${mono ? 'font-mono' : ''}`} style={{ color: C.navy }} data-testid={testid}>{value}</p>
  </div>
);

const Row = ({ k, v, mono }) => (
  <div className="flex gap-2">
    <span className="w-44 shrink-0" style={{ color: C.slate }}>{k}</span>
    <span className={`${mono ? 'font-mono break-all text-[10px]' : ''} font-medium`}>{v}</span>
  </div>
);
